import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { UploadedFile } from '@components/file-field';
import { DefaultFile } from '@components/file-field/types';
import { createForm, useCreateFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { HtmlContent } from '@components/html-content';
import { IconEmail } from '@components/icons/email';
import { IconSend } from '@components/icons/send';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingVariables,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { EditorValue } from '@components/text-editors/text-editor';
import { useScopedTranslate } from '@locale/translate';
import { getRawTextFromHtml } from '@utils/browser';
import { fileNameFromPath } from '@utils/strings';

import { useEmailAddressOptions } from 'api/company/hooks';
import { createTravellerJourneyActionDocumentPath } from 'api/documents/requests';
import { useEmailDelivery } from 'api/emails/hooks';
import { useUploadFile } from 'api/files/hooks';
import { useActionEmailTemplate } from 'api/journeys/hooks';
import {
  useActivityLogs,
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import {
  describeDeliveryFailure,
  describeDeliverySummary,
  getDeliverySummary,
} from './delivery-status';
import {
  buildEmailPreviewPropsFromTravellerAction,
  usePreviewEmailModal,
} from './email-preview-modal';
import { PreviewEmailMenuItemView } from './preview-email-menu-item';

import {
  ActionErrorMessage,
  ActionResultCard,
  ActionProgressBar,
  AttachmentList,
  FormSidebarFooter,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TravellerActionHeader,
  TriggerMethod,
  isActionError,
  useActionTabs,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';
import { EmailAddressOptions, SendFromEmailSelect } from '../shared/send-from-email-select';
import {
  brokenChipStyles,
  renderWithVariableChips,
  variableChipStyles,
} from '../shared/variable-chips';

const ACTION_TYPE = ACTION_TYPES.send_email;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  const emailAddressOptions = useEmailAddressOptions();
  const { data: activityLogs } = useActivityLogs(shortId);
  const activityCount = activityLogs?.length ?? 0;
  const action = travellerActionQuery.data;

  const { tabs } = useActionTabs({
    actionId: shortId ?? '',
    activityCount,
    display: action ? <DetailsContent action={action} /> : <></>,
    edit: action ? (
      <FormContent action={action} emailAddressOptions={emailAddressOptions} />
    ) : (
      <></>
    ),
  });

  if (travellerActionQuery.isLoading || emailAddressOptions.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !action) {
    return <div>{t('dataNotFound')}</div>;
  }

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE} tabs={tabs}>
        <TravellerActionActions
          action={action}
          customMenuItems={
            <PreviewEmailMenuItemView
              previewProps={buildEmailPreviewPropsFromTravellerAction(action)}
            />
          }
        />
      </TravellerActionHeader>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const showEmailPreview = usePreviewEmailModal();

  return (
    <Stack space={6}>
      {/* The failure detail lives in the card, next to the message it is
          about, so the outer bar stays a bare progress summary. */}
      <ActionProgressBar action={action} isError={isError} isCard={false} hideErrorMessage />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <EmailDetailCard action={action} />
        </Stack>
      </HideContainer>
      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('sendEmail')}
        retryText={t('resendEmail')}
        onPreview={() => showEmailPreview(buildEmailPreviewPropsFromTravellerAction(action))}
      />
    </Stack>
  );
};

type EmailData = {
  description: string;
  isAutomatic: boolean;
  sendFromEmailId?: string;
  fromEmail?: string;
  recipients: EditorValue<{ variables?: FieldMappingVariables }>[];
  cc: EditorValue<{ variables?: FieldMappingVariables }>[];
  bcc: EditorValue<{ variables?: FieldMappingVariables }>[];
  replyTo: EditorValue<{ variables?: FieldMappingVariables }>[];
  subject: EditorValue<{ variables?: FieldMappingVariables }>;
  content: EditorValue<{ variables?: FieldMappingVariables }>;
  attachments: EditorValue<{ variables?: FieldMappingVariables }>;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
};

const Form = createForm<EmailData>();

type FormContentProps = ContentProps & {
  emailAddressOptions: EmailAddressOptions;
};

const FormContent = ({ action, emailAddressOptions }: FormContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const [showCC, setShowCC] = useState((action.values?.cc?.length ?? 0) > 0);
  const [showBCC, setShowBCC] = useState((action.values?.bcc?.length ?? 0) > 0);
  const [showReplyTo, setShowReplyTo] = useState((action.values?.replyTo?.length ?? 0) > 0);
  const baseVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    excludedTypes: ['file', 'file_multi'],
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    types: ['email'],
  });
  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    types: ['file', 'file_multi'],
  });
  const fieldMappingId = getFieldMappingId(action.journeyId);

  const uploadMutation = useUploadFile(createTravellerJourneyActionDocumentPath(action), true);

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });
  const handleSave = (data: EmailData) => {
    return editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        sendFromEmailId: data.sendFromEmailId ?? [],
        recipients: data.recipients ?? [],
        cc: data.cc ?? [],
        bcc: data.bcc ?? [],
        replyTo: data.replyTo ?? [],
        subject: data.subject?.value ? data.subject : {},
        content: data.content ?? {},
        attachments: data.attachments ?? [],
      },
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            hideLabel
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            formStore={formStore}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('emailContent')}>
          <Stack space={4}>
            <SendFromEmailSelect
              Form={Form}
              emailAddressOptions={emailAddressOptions}
              defaultValue={action.values?.sendFromEmailId}
            />
            <Form.EmailListField
              id="recipients"
              label={t('sendTo')}
              defaultValue={action.values?.recipients ?? []}
              variableGroups={emailVariableGroups}
              validators={['required']}
              info={t('sendToInfo')}
              fieldMappingId={fieldMappingId}
              labelRightContent={
                <Stack orientation="horizontal" space={2}>
                  {!showCC && (
                    <ActionText
                      font="semibold14"
                      color="textHelper"
                      hoverColor="elementBlue100"
                      underline={false}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowCC(true);
                      }}
                    >
                      {t('cc')}
                    </ActionText>
                  )}
                  {!showBCC && (
                    <ActionText
                      font="semibold14"
                      color="textHelper"
                      hoverColor="elementBlue100"
                      underline={false}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowBCC(true);
                      }}
                    >
                      {t('bcc')}
                    </ActionText>
                  )}
                  {!showReplyTo && (
                    <ActionText
                      font="semibold14"
                      color="textHelper"
                      hoverColor="elementBlue100"
                      underline={false}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowReplyTo(true);
                      }}
                    >
                      {t('replyTo')}
                    </ActionText>
                  )}
                </Stack>
              }
            />
            {showCC && (
              <Form.EmailListField
                id="cc"
                label={t('cc')}
                variableGroups={emailVariableGroups}
                defaultValue={action.values?.cc}
                key={`ccRecipients-${String(action.shortId)}`}
                fieldMappingId={fieldMappingId}
              />
            )}
            {showBCC && (
              <Form.EmailListField
                id="bcc"
                label={t('bcc')}
                defaultValue={action.values?.bcc}
                variableGroups={emailVariableGroups}
                key={`bccRecipients-${String(action.shortId)}`}
                fieldMappingId={fieldMappingId}
              />
            )}
            {showReplyTo && (
              <Form.EmailListField
                id="replyTo"
                label={t('replyTo')}
                defaultValue={action.values?.replyTo}
                variableGroups={emailVariableGroups}
                key={`replyToRecipients-${String(action.shortId)}`}
                fieldMappingId={fieldMappingId}
              />
            )}
            <Form.FieldMappingTextEditor
              id="subject"
              label={t('subject')}
              plaintext
              singleLine
              variableGroups={baseVariableGroups}
              defaultValue={action.values?.subject}
              validators={['required']}
              info={t('subjectInfo')}
              fieldMappingId={fieldMappingId}
            />
            <Form.FieldMappingTextEditor
              id="content"
              label={t('messageBody')}
              variableGroups={baseVariableGroups}
              defaultValue={action.values?.content}
              validators={['required']}
              fieldMappingId={fieldMappingId}
            />
            <Form.FieldMappingFileListField
              id="attachments"
              label={t('emailAttachment')}
              defaultValue={action.values?.attachments}
              variableGroups={fileVariableGroups}
              onSelect={uploadMutation.mutateAsync}
              fieldMappingId={fieldMappingId}
            />
          </Stack>
        </HideContainer>
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

type Attachment = Omit<DefaultFile, 'id'> & {
  canView?: boolean;
};

export const EmailDetailCard = ({ action }: ContentProps) => {
  const emailDeliveryQuery = useEmailDelivery(action.shortId);
  const { t } = useScopedTranslate(sidebarLocale);
  const showPreviewModal = usePreviewEmailModal();

  const emailAddressOptions = useEmailAddressOptions();
  const emailAddressUsed = emailAddressOptions.options.find(
    (option) => option.value === action.data.sendFromEmailId
  );
  const sendFromAddress =
    action.data?.fromEmail ??
    (emailAddressUsed
      ? `${emailAddressUsed.label as string} <${emailAddressUsed.helpText}>`
      : t('systemDefaultEmail'));
  const recipients = ((action.data?.recipients ?? []) as string[]).flatMap((receipient) =>
    receipient.split(',')
  );
  const cc = ((action.data?.cc ?? []) as string[]).flatMap((cc) => cc.split(','));
  const bcc = ((action.data?.bcc ?? []) as string[]).flatMap((bcc) => bcc.split(','));
  const replyTo = ((action.data?.replyTo ?? []) as string[]).flatMap((replyTo) =>
    replyTo.split(',')
  );
  const isError = isActionError(action);
  const status = getActionStatus(action);
  const showFilePreviewModal = usePreviewFileModal();

  // Mailgun delivery events, when we have them; a completed action without
  // them only proves the SMTP server accepted the message, so it shows "Sent".
  const emailDelivery = emailDeliveryQuery.data ?? [];
  const failedDeliveries = emailDelivery.filter((entry) => entry.status === 'failed');
  const deliverySummary = getDeliverySummary(recipients, emailDelivery);
  const failureReasons = failedDeliveries.map((entry) => describeDeliveryFailure(entry, t));

  const progressText =
    status === 'completed'
      ? describeDeliverySummary(deliverySummary, t)
      : isError
      ? // A bounce means it did leave; the error block above says what then
        // happened to it. A send that never left keeps "Error".
        failedDeliveries.length > 0
        ? t('sent')
        : t('error')
      : status === 'in_progress'
      ? // Only reachable after the message has gone out -- a managed domain
        // waits here for Mailgun to report per-recipient delivery.
        t('awaitingDelivery')
      : t('notSent');

  const attachmentsValues = (action.values?.attachments ?? []).map((att: VariableValue) =>
    att.variables ? Object.values(att.variables)[0].label : att.value
  );

  const emailMessage = buildEmailPreviewPropsFromTravellerAction(action);
  const brokenLabelSet = emailMessage.brokenLabels?.length
    ? new Set(emailMessage.brokenLabels)
    : undefined;

  const actionDataAttachments: (Omit<DefaultFile, 'id'> | string)[] = (
    action.data?.attachments ?? []
  ).flat();

  const attachments: Attachment[] = actionDataAttachments.map((att) => {
    if (typeof att == 'string') {
      return { name: fileNameFromPath(att), src: att, canView: att.startsWith('http') };
    }
    return { ...att, canView: true };
  });
  const hasAttachments =
    (status === 'completed' ? attachments.length : attachmentsValues.length) > 0;

  return (
    <ActionResultCard
      action={action}
      title={t('emailMessage')}
      icon={<IconEmail />}
      onPreview={() => showPreviewModal(emailMessage)}
      hideStatus
    >
      {(isError || failureReasons.length > 0) && (
        <ActionErrorMessage
          errorMessage={{
            title: actionErrorTitle[action.action],
            description: failureReasons.length > 0 ? failureReasons : action.result?.message,
            date: action.result?.dateFailed,
          }}
        />
      )}
      <ActionProgressBar
        action={action}
        progressText={progressText}
        isError={isError}
        isCard={false}
        hideErrorMessage
        progressVariant={failedDeliveries.length > 0 ? 'error' : undefined}
        // A bounced action has no success timestamp to show here, and a bare
        // ratio next to "Sent" tells the reader nothing.
        progressPercentage={isError ? '' : undefined}
      />
      <LabelTextGroup label={t('sendFrom')} text={sendFromAddress} />
      <LabelTextGroup label={t('sendTo')} text={recipients} />
      <Separator />
      {cc.length > 0 && <LabelTextGroup label={t('cc')} text={cc} />}
      {bcc.length > 0 && <LabelTextGroup label={t('bcc')} text={bcc} />}
      {replyTo.length > 0 && <LabelTextGroup label={t('replyTo')} text={replyTo} />}
      <Stack space={1}>
        <Text font="semibold14" text={t('subject')} />
        <Text
          font="regular14"
          text={renderWithVariableChips(
            getRawTextFromHtml(action.data?.subject || action.values?.subject?.value || '') ||
              t('notSet'),
            brokenLabelSet
          )}
        />
      </Stack>
      <Stack space={1}>
        <Text font="semibold14" text={t('message')} />
        <VariableHighlightedContent>
          <EmailMessageBody action={action} />
        </VariableHighlightedContent>
      </Stack>

      {hasAttachments && (
        <Stack space={1}>
          <Text font="semibold14" text={t('emailAttachment')} />
          {status === 'completed' ? (
            <>
              {attachments.map((attachment, idx) => (
                <UploadedFile
                  key={idx}
                  isDisabled={!attachment.canView}
                  name={attachment.name}
                  path={attachment.src}
                  onPreview={() => {
                    showFilePreviewModal({ formFile: attachment, filesList: [attachment] });
                  }}
                />
              ))}
            </>
          ) : (
            <AttachmentList attachments={attachmentsValues} showAsCards />
          )}
        </Stack>
      )}
    </ActionResultCard>
  );
};

const EmailMessageBody = ({ action }: ContentProps) => {
  // Reuse the modal's server-sanitized preview and extract just the body so the
  // sidepanel and modal stay in sync. It's the only value we inject as HTML;
  // until it arrives (or if id="content" is missing) show the template as text.
  const { data: previewHtml } = useActionEmailTemplate('traveller', action.shortId);
  const innerHtml = useMemo(() => extractEmailBodyHtml(previewHtml), [previewHtml]);
  if (innerHtml) {
    return <HtmlContent font="regular14" html={innerHtml} />;
  }
  return <Text font="regular14" text={getRawTextFromHtml(action.values?.content?.value ?? '')} />;
};

const extractEmailBodyHtml = (previewHtml: string | undefined): string => {
  if (!previewHtml) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(previewHtml, 'text/html');
  // "content" id is a contract with the email wrapper template (constants.py);
  // a backend test fails if it's renamed. Missing id -> the text fallback above.
  return doc.getElementById('content')?.innerHTML ?? '';
};

const VariableHighlightedContent = styled.div({
  '& code': variableChipStyles,
  // The backend wraps a broken variable (deleted/severed source) in <ins>; render
  // that chip red, matching the preview modal.
  '& ins': {
    textDecoration: 'none',
  },
  '& ins > code, & ins > code *': brokenChipStyles,
});

const sidebarLocale = {
  en: {
    sendEmail: 'Send email',
    dataNotFound: 'Data not found',
    description: 'Description',
    message: 'Message',
    messageBody: 'Message (body)',
    resendEmail: 'Resend email',
    subject: 'Email subject',
    sendFrom: 'Send from',
    systemDefaultEmail: 'System default email',
    sendTo: 'Send to',
    inProgress: 'In progress',
    awaitingDelivery: 'Sent, awaiting delivery',
    notSent: 'Not sent',
    error: 'Error',
    delivered: 'Delivered',
    sent: 'Sent',
    deliveryFailed: 'Delivery failed',
    deliveryFailureReasonSuppressBounce:
      "earlier emails to this address didn't arrive, so we've stopped sending to it. Check the spelling, or unblock it under Settings → Email domain → Blocked addresses.",
    deliveryFailureReasonSuppressComplaint:
      "they marked our emails as spam, so we've stopped sending to them. You'll need to reach them another way.",
    deliveryFailureReasonSuppressUnsubscribe:
      'they unsubscribed, so this email was not sent to them.',
    deliveryFailureReasonBounce:
      "the address didn't accept it. It may be misspelled, or the mailbox may no longer exist.",
    emailMessage: 'Email message',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    emailContent: 'Email content',
    sendToInfo: 'One or more recipient for your email',
    subjectInfo: 'Subject of your email, with optional variables',
    emailAttachment: 'Email attachment',
    cc: 'CC',
    bcc: 'BCC',
    replyTo: 'Reply-To',
    notSet: 'Not set',
  },
};
